import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/Button';
import { toggleDialog } from '../../../redux/Slices/Global/globalSlice';
import { useCreatePlaylistMutation } from '../../../queries';
import '../../Campaign/css/setup.css';

/**
 * "New Playlist" dialog, opened from PlaylistGallery.jsx. Restyled ONLY —
 * same `useCreatePlaylistMutation()` (POST /playlist/create, name only,
 * navigates to /playlist/{id}/show on success) and same `openDialog
 * .createPlaylist` flag PlaylistGallery.jsx already toggles — as the
 * prototype's create-sheet (.claude/pp-playlist-page-prototype.html's
 * `#newSheet`): name input, inline error on empty submit, Enter submits.
 * PlaylistGallery.jsx's own list/grid rendering is untouched.
 */
const Create = () => {
    const { t } = useTranslation();
    const { openDialog } = useSelector((state) => state.global);
    const createPlaylist = useCreatePlaylistMutation();
    const dispatch = useDispatch();

    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [show, setShow] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (openDialog.createPlaylist) {
            setName('');
            setError('');
            const raf = requestAnimationFrame(() => setShow(true));
            const focusTimer = setTimeout(() => inputRef.current?.focus(), 250);
            return () => { cancelAnimationFrame(raf); clearTimeout(focusTimer); };
        }
        setShow(false);
    }, [openDialog.createPlaylist]);

    const hideDialog = () => {
        setShow(false);
        setTimeout(() => dispatch(toggleDialog('createPlaylist')), 260);
    };

    const submit = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError(t('playlist.create_name_error'));
            return;
        }
        await createPlaylist.mutateAsync({ name: trimmed });
    };

    if (!openDialog.createPlaylist) return null;

    return (
        <>
            <div className={`pp-cb-scrim ${show ? 'show' : ''}`} onClick={hideDialog}></div>
            <div className={`pp-cb-sheet ${show ? 'show' : ''}`} role="dialog" aria-label={t('playlist.new_playlist')}>
                <div className="pp-cb-grabber"></div>
                <h3>{t('playlist.new_playlist')}</h3>
                <div className="pp-cb-sheet-sub">{t('playlist.create_sub')}</div>

                <input
                    ref={inputRef}
                    className="pp-cb-create-in"
                    value={name}
                    maxLength={50}
                    placeholder={t('playlist.create_name_placeholder')}
                    onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                />
                <div className="pp-cb-create-err">{error}</div>

                <Button label={t('buttons.create')} className="w-100" onClick={submit} loading={createPlaylist.isLoading} />
                <Button label={t('buttons.cancel')} outlined className="w-100" style={{ marginTop: 10 }} onClick={hideDialog} disabled={createPlaylist.isLoading} />
            </div>
        </>
    );
};
export default Create;
